import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const TaxForm = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [isSigned, setIsSigned] = useState(false);
  const [sinFile, setSinFile] = useState(null);
  const [taxFile, setTaxFile] = useState(null);
  const [files, setFiles] = useState({}); // for display

  // 🔄 Fetch uploaded file names
  const fetchTaxDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/taxform/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFiles(res.data || {});
    } catch (err) {
      console.error("Fetch tax docs failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchTaxDocs();
  }, [fetchTaxDocs]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 5; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "tax_form";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  // Upload
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sinFile && !taxFile) {
      alert("Please select at least one file to upload.");
      return;
    }

    const formData = new FormData();
    if (sinFile) formData.append("tax_sin_doc", sinFile);
    if (taxFile) formData.append("tax_form_doc", taxFile);
    formData.append("userId", userId);

    try {
      await axios.post(
        `${API_BASE}/api/taxform/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert("Upload successful!");
      fetchTaxDocs();
      setSinFile(null);
      setTaxFile(null);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      {/* 🔝 Header Section with Status + Cancel Button */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>Tax Form</span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>
      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start">
            {/* Left side can stay empty or hold something else */}
          </div>

          <div className="col-4 text-center">
            <h3 className="mb-0">Tax Form</h3>
          </div>

          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>
        <p>
          CCRA link for the forms:{" "}
          <a
            href="https://www.canada.ca/en/revenue-agency/services/forms-publications/td1-personal-tax-credits-returns/td1-forms-pay-received-on-january-1-later.html"
            target="_blank"
            rel="noreferrer"
          >
            TD1 Forms Reference
          </a>
        </p>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="mb-3">
            <label className="form-label">Upload SIN (PDF):</label>
            <input
              type="file"
              className="form-control mb-4"
              accept="application/pdf"
              disabled={!isAdmin && isSigned}
              onChange={(e) => setSinFile(e.target.files[0])}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Upload TD1 / Tax Form (PDF):</label>
            <input
              type="file"
              className="form-control mb-4"
              accept="application/pdf"
              disabled={!isAdmin && isSigned}
              onChange={(e) => setTaxFile(e.target.files[0])}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(!sinFile && !taxFile) || (!isAdmin && isSigned)}
            >
              Upload
            </button>
          </div>
        </form>

        {(files.tax_sin_doc || files.tax_form_doc) && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Uploaded Version</div>
            {files.tax_sin_doc && (
              <div>
                <a
                  href={`${API_BASE}/uploads/taxforms/${
                    files.tax_sin_doc || ""
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={files.tax_sin_original || files.tax_sin_doc}
                >
                  {files.tax_sin_original}
                </a>
              </div>
            )}
            {files.tax_form_doc && (
              <div>
                <a
                  href={`${API_BASE}/uploads/taxforms/${files.tax_form_doc}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={files.tax_form_original || files.tax_form_doc}
                >
                  {files.tax_form_original}
                </a>
              </div>
            )}
          </div>
        )}
        {userId && (
          <>
            <SignatureManager
              taskName="tax_form"
              taskId={5}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="tax_form"
              taskId={5}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default TaxForm;
